/**
 * Membership Badge Component
 * ==========================
 * Premium tier badges for NearU members.
 * Free | Gold | Diamond (Silver removed - maps to Gold for backwards compatibility)
 */

import React from 'react';
import { Crown, Gem, Star } from 'lucide-react';

const MembershipBadge = ({ 
  tier = 'free', 
  size = 'medium',
  showLabel = true,
  style = {}
}) => {
  // Map legacy 'silver' to 'gold'
  const normalizedTier = tier === 'silver' ? 'gold' : tier;
  
  const sizes = {
    small: { padding: '3px 8px', fontSize: '9px', iconSize: 10, gap: '4px' },
    medium: { padding: '5px 12px', fontSize: '11px', iconSize: 12, gap: '5px' },
    large: { padding: '6px 16px', fontSize: '13px', iconSize: 14, gap: '6px' }
  };

  const tierConfig = {
    free: {
      name: 'Free',
      gradient: 'linear-gradient(135deg, #4A4A4A 0%, #2D2D2D 50%, #1A1A1A 100%)',
      textColor: '#E6D7C3',
      icon: Star,
      shadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
      border: '1px solid rgba(230, 215, 195, 0.3)'
    },
    gold: {
      name: 'Gold',
      gradient: 'linear-gradient(135deg, #FFF8DC 0%, #FFD700 20%, #DAA520 50%, #B8860B 80%, #996515 100%)',
      textColor: '#5D4E37',
      icon: Crown,
      shadow: '0 2px 12px rgba(218, 165, 32, 0.4)',
      border: '1px solid rgba(218, 165, 32, 0.4)'
    },
    diamond: {
      name: 'Diamond',
      gradient: 'linear-gradient(135deg, #F0F8FF 0%, #E6F3FF 15%, #B9F2FF 35%, #89CFF0 55%, #00BFFF 75%, #1E90FF 100%)',
      textColor: '#1A5276',
      icon: Gem,
      shadow: '0 2px 12px rgba(30, 144, 255, 0.4), 0 0 20px rgba(185, 242, 255, 0.3)',
      border: '1px solid rgba(137, 207, 240, 0.5)'
    }
  };

  const config = tierConfig[normalizedTier] || tierConfig.free;
  const sizeConfig = sizes[size] || sizes.medium;
  const Icon = config.icon;

  return (
    <div 
      data-testid={`membership-badge-${normalizedTier}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: sizeConfig.gap,
        padding: sizeConfig.padding,
        background: config.gradient,
        borderRadius: '20px',
        boxShadow: config.shadow,
        border: config.border,
        ...style
      }}
    >
      <Icon 
        size={sizeConfig.iconSize} 
        color={config.textColor}
        style={{ 
          filter: normalizedTier === 'diamond' ? 'drop-shadow(0 0 2px rgba(30, 144, 255, 0.5))' : 'none'
        }}
      />
      {showLabel && (
        <span style={{
          fontSize: sizeConfig.fontSize,
          fontWeight: '700',
          color: config.textColor,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          textShadow: normalizedTier === 'diamond' ? '0 0 1px rgba(255, 255, 255, 0.5)' : 'none'
        }}>
          {config.name}
        </span>
      )}
    </div>
  );
};

export default MembershipBadge;
